export type TokenPlaceholderOptions = {
  size?: number;
  bgColor?: string; // background (circle/square)
  fgColor?: string; // foreground (text or blocks)
  shape?: "circle" | "square";
  variant?: "plain" | "question" | "symbol" | "blocky";
  symbol?: string; // used when variant === 'symbol'
  border?: boolean;
  borderColor?: string;
  fontFamily?: string;
};

/**
 * Generate a lightweight SVG data URL as a token-logo fallback.
 * Defaults to a gray circle. Can render a question mark, a symbol (e.g., first letter),
 * or a simple blocky pattern. Returned value is a `data:image/svg+xml;utf8,...` URL.
 */
export function getTokenPlaceholderDataUrl(
  options: TokenPlaceholderOptions = {},
): string {
  const {
    size = 64,
    bgColor = "#2F3136",
    fgColor = "#9AA0A6",
    shape = "circle",
    variant = "plain",
    symbol,
    border = false,
    borderColor = "rgba(255,255,255,0.08)",
    fontFamily =
      'system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
  } = options;

  const s = Math.max(16, Math.floor(size));
  const center = s / 2;
  const radius = center - (border ? 1 : 0);

  const shapeNode = shape === "square"
    ? `<rect x="0" y="0" width="${s}" height="${s}" rx="${
      Math.floor(s * 0.15)
    }" fill="${bgColor}" ${
      border ? `stroke="${borderColor}" stroke-width="1"` : ""
    }></rect>`
    : `<ellipse cx="${center}" rx="${radius}" cy="${center}" ry="${radius}" fill="${bgColor}" ${
      border ? `stroke="${borderColor}" stroke-width="1"` : ""
    }></ellipse>`;

  let overlay = "";
  const fontAttr = escapeXml(fontFamily);

  if (variant === "question") {
    const fontSize = Math.floor(s * 0.55);
    overlay = `<text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-family="${fontAttr}" font-size="${fontSize}" font-weight="700" fill="${fgColor}">?</text>`;
  } else if (variant === "symbol") {
    const char = (symbol || "?").slice(0, 1).toUpperCase();
    const fontSize = Math.floor(s * 0.5);
    overlay = `<text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-family="${fontAttr}" font-size="${fontSize}" font-weight="700" fill="${fgColor}">${escapeXml(char)}</text>`;
  } else if (variant === "blocky") {
    // Simple 5x5 blocky pattern centered in the shape
    const grid = 5;
    const padding = Math.floor(s * 0.18);
    const cell = Math.floor((s - padding * 2) / grid);
    const start = Math.floor((s - cell * grid) / 2);
    const seed = (symbol || "").split("").reduce(
      (acc, ch) => acc + ch.charCodeAt(0),
      0,
    );
    const blocks: string[] = [];
    for (let y = 0; y < grid; y++) {
      for (let x = 0; x < grid; x++) {
        // Deterministic on x,y + seed
        const on = ((x * 31 + y * 17 + seed) % 7) < 3; // ~40% fill
        if (on) {
          const rx = start + x * cell;
          const ry = start + y * cell;
          blocks.push(
            `<rect x="${rx}" y="${ry}" width="${cell}" height="${cell}" fill="${fgColor}" opacity="0.85"/>`,
          );
        }
      }
    }
    overlay = blocks.join("");
  }

  const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="token placeholder">
  ${shapeNode}
  ${overlay}
</svg>`;

  try {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  } catch (error) {
    // Fallback: use base64 encoding if encodeURIComponent fails
    console.warn('Failed to encode SVG with encodeURIComponent, using base64 fallback:', error);
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }
}

function escapeXml(str: string): string {
  if (!str || typeof str !== 'string') {
    return '';
  }
  
  return str
    // Remove any control characters and non-printable characters
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x84\x86-\x9F]/g, '')
    // Handle null bytes and other potentially problematic characters
    .replace(/\0/g, '')
    // XML escaping
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}
